import React from "react";
import { useNavigate } from "react-router-dom";

import { Box, Grid, Typography } from "@mui/material";
import Page from "./Page";
import useSurveyMetrics from "../../hooks/useSurveyMetrics";
import SurveyMetricsFilters from "../dashboard/SurveyMetricsFilters";
import SurveyMetricCard from "../dashboard/SurveyMetricCard";
import DailyTrendChart from "../dashboard/DailyTrendChart";

const SurveyMetrics = () => {
  const navigate = useNavigate();

  const {
    filters,
    updateClientFilter,
    updateProjectFilter,
    updateSurveyFilter,
    updateDateRange,
    clients,
    projects,
    surveys,
    loadingDropdowns,
    metricsData,
  } = useSurveyMetrics();

  const metrics = metricsData.data;
  const isLoading = metricsData.isLoading;

  const handleToolBarClick = (item) => {
    switch (item) {
      case "callDashboard":
        navigate("/home");
        break;

      case "syncDashboard":
        navigate("/syncdashboard");
        break;

      case "surveyDashboard":
        navigate("/surveymetrics");
        break;

      case "orderDashboard":
        navigate("/orderdashboard");
        break;

      case "clientDashboard":
        navigate("/clientdashboard");
        break;

      case "repDashboard":
        navigate("/repdashboard");
        break;
    }
  };

  return (
    <Page
      pageName="surveyDashboard"
      includeToolbar={true}
      onToolItemClick={handleToolBarClick}
      items={[
        { name: "callDashboard", iconName: "CalendarMonth" },
        { name: "syncDashboard", iconName: "CloudSync" },
        { name: "orderDashboard", iconName: "ShoppingCart" },
        { name: "clientDashboard", iconName: "Business" },
        { name: "repDashboard", iconName: "People" },
      ]}
    >
      <Box sx={{ p: 2 }}>
        {/* FILTERS */}
        <SurveyMetricsFilters
          filters={filters}
          onClientChange={updateClientFilter}
          onProjectChange={updateProjectFilter}
          onSurveyChange={updateSurveyFilter}
          onDateRangeChange={updateDateRange}
          clients={clients}
          projects={projects}
          surveys={surveys}
          loading={loadingDropdowns}
        />

        {/* ERROR HANDLING */}
        {metricsData.error && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#ffebee",
              color: "#c62828",
              borderRadius: "4px",
            }}
          >
            <Typography variant="body2">
              Error loading metrics: {metricsData.error}
            </Typography>
          </Box>
        )}

        {/* METRICS GRID */}
        <Grid
          container
          spacing={2}
        >
          {/* ROW 1: COMPLETION METRICS */}
          <Grid
            xs={12}
            sm={6}
            md={2.4}
          >
            <SurveyMetricCard
              label="Surveys Scheduled"
              value={metrics?.totalSent || 0}
              isLoading={isLoading}
            />
          </Grid>
          <Grid
            xs={12}
            sm={6}
            md={2.4}
          >
            <SurveyMetricCard
              label="Surveys Completed"
              value={metrics?.completed || 0}
              isLoading={isLoading}
            />
          </Grid>
          <Grid
            xs={12}
            sm={6}
            md={2.4}
          >
            <SurveyMetricCard
              label="Completion Rate"
              value={metrics?.completionRate || 0}
              unit="%"
              isLoading={isLoading}
            />
          </Grid>
          <Grid
            xs={12}
            sm={6}
            md={2.4}
          >
            <SurveyMetricCard
              label="Surveys Incomplete"
              value={metrics?.incomplete || 0}
              isLoading={isLoading}
            />
          </Grid>
          <Grid
            xs={12}
            sm={6}
            md={2.4}
          >
            <SurveyMetricCard
              label="Avg Completion Time"
              value={metrics?.avgCompletionTime || 0}
              unit=" min"
              isLoading={isLoading}
            />
          </Grid>

          {/* ROW 2: DURATION VARIANCE METRICS */}
          <Grid
            xs={12}
            sm={6}
            md={2.4}
          >
            <SurveyMetricCard
              label="Avg Estimated Duration"
              value={metrics?.avgEstimatedDuration || 0}
              unit=" min"
              isLoading={isLoading}
            />
          </Grid>
          <Grid
            xs={12}
            sm={6}
            md={2.4}
          >
            <SurveyMetricCard
              label="Avg Actual Duration"
              value={metrics?.avgActualDuration || 0}
              unit=" min"
              isLoading={isLoading}
            />
          </Grid>
          <Grid
            xs={12}
            sm={6}
            md={2.4}
          >
            <SurveyMetricCard
              label="Duration Variance"
              value={metrics?.durationVariance || 0}
              unit=" min"
              isLoading={isLoading}
            />
          </Grid>
          <Grid
            xs={12}
            sm={6}
            md={2.4}
          >
            <SurveyMetricCard
              label="% Over Estimated"
              value={metrics?.percentOverEstimated || 0}
              unit="%"
              isLoading={isLoading}
            />
          </Grid>

          {/* ROW 3: DAILY TREND CHART */}
          <Grid xs={12}>
            <DailyTrendChart
              data={metrics?.dailyTrend || []}
              isLoading={isLoading}
            />
          </Grid>
        </Grid>
      </Box>
    </Page>
  );
};

export default SurveyMetrics;
